import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Group,
  Loader,
  Modal,
  Notification,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Badge,
  Center,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconAlertTriangle,
  IconCar,
  IconCheck,
  IconEdit,
  IconPlus,
  IconRefresh,
  IconRoad,
  IconTruckReturn,
  IconX,
  IconTrash, // Silme ikonu eklendi
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosConfig';

const VehicleTracking = () => {
  const navigate = useNavigate();

  const [fleet, setFleet] = useState([]);
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAssignEditModalOpen, setIsAssignEditModalOpen] = useState(false);

  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const [confirmInfo, setConfirmInfo] = useState({
    show: false,
    id: null,
  });

  // Silme işlemi için yeni onay modalı state'i
  const [deleteConfirmInfo, setDeleteConfirmInfo] = useState({
    show: false,
    id: null,
  });

  const [newVehicle, setNewVehicle] = useState({
    plate: '',
    model: '',
  });

  const [editingVehicle, setEditingVehicle] = useState({
    id: null,
    plate: '',
    model: '',
  });

  const [assignmentForm, setAssignmentForm] = useState({
    vehicle_id: '',
    driver_name: '',
    reason: '',
  });

  const [editingAssignment, setEditingAssignment] = useState({
    id: null,
    driver_name: '',
    reason: '',
  });

  const [submittingVehicle, setSubmittingVehicle] = useState(false);
  const [submittingVehicleEdit, setSubmittingVehicleEdit] = useState(false);
  const [submittingAssign, setSubmittingAssign] = useState(false);
  const [submittingAssignmentEdit, setSubmittingAssignmentEdit] = useState(false);
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [submittingDelete, setSubmittingDelete] = useState(false); // Silme yüklenme durumu

  useEffect(() => {
    refreshData();
  }, []);

  const showCustomAlert = (type, message) => {
    setAlertInfo({ show: true, type, message });

    if (type === 'success') {
      setTimeout(() => {
        setAlertInfo({ show: false, type: '', message: '' });
      }, 2000);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      const [fleetRes, assignRes] = await Promise.all([
        api.get('/istanbul-office/vehicles'), 
        api.get('/istanbul-office/reports/vehicles'),
      ]);

      setFleet(fleetRes.data || []);
      
      const activeTasks = (assignRes.data || []).filter(task => task.return_time === null);
      setActiveAssignments(activeTasks);
    } catch (err) {
      console.error('Veri yenileme hatası:', err);
      showCustomAlert(
        'error',
        err.response?.data?.detail || 'Veriler alınamadı.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();

    if (!newVehicle.plate.trim() || !newVehicle.model.trim()) {
      showCustomAlert('error', 'Lütfen plaka ve model bilgisini giriniz.');
      return;
    }

    setSubmittingVehicle(true);
    try {
      await api.post('/istanbul-office/vehicles', {
        plate: newVehicle.plate.trim(),
        model: newVehicle.model.trim(),
        is_available: true
      });

      showCustomAlert('success', 'Araç başarıyla kaydedildi.');
      setIsFormModalOpen(false);
      setNewVehicle({ plate: '', model: '' });
      refreshData();
    } catch (error) {
      console.error('Araç ekleme hatası:', error);

      if (error.response?.status === 400) {
        showCustomAlert(
          'error',
          error.response?.data?.detail || 'Bu plaka zaten sistemde kayıtlı.'
        );
      } else {
        showCustomAlert(
          'error',
          error.response?.data?.detail || 'Sunucu hatası oluştu.'
        );
      }
    } finally {
      setSubmittingVehicle(false);
    }
  };

  const openEditModal = (vehicle) => {
    setEditingVehicle({
      id: vehicle.id,
      plate: vehicle.plate || '',
      model: vehicle.model || '',
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateVehicle = async (e) => {
    e.preventDefault();

    if (!editingVehicle.plate.trim() || !editingVehicle.model.trim()) {
      showCustomAlert('error', 'Plaka ve model alanları zorunludur.');
      return;
    }

    setSubmittingVehicleEdit(true);
    try {
      await api.put(`/istanbul-office/vehicles/${editingVehicle.id}`, {
        plate: editingVehicle.plate.trim(),
        model: editingVehicle.model.trim(),
      });

      showCustomAlert('success', 'Araç bilgileri güncellendi.');
      setIsEditModalOpen(false);
      refreshData();
    } catch (error) {
      console.error('Araç güncelleme hatası:', error);
      showCustomAlert(
        'error',
        error.response?.data?.detail || 'Güncelleme başarısız.'
      );
    } finally {
      setSubmittingVehicleEdit(false);
    }
  };

  // Yeni Araç Silme Fonksiyonu
  const confirmDeleteVehicle = async () => {
    if (!deleteConfirmInfo.id) return;

    setSubmittingDelete(true);
    try {
      await api.delete(`/istanbul-office/vehicles/${deleteConfirmInfo.id}`);
      showCustomAlert('success', 'Araç başarıyla silindi.');
      refreshData();
    } catch (error) {
      console.error('Araç silme hatası:', error);
      showCustomAlert(
        'error',
        error.response?.data?.detail || 'Araç silinemedi. (Görev geçmişi olabilir)'
      );
    } finally {
      setSubmittingDelete(false);
      setDeleteConfirmInfo({ show: false, id: null });
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();

    if (!assignmentForm.vehicle_id) {
      showCustomAlert('error', 'Lütfen bir araç seçin.');
      return;
    }

    if (!assignmentForm.driver_name.trim() || !assignmentForm.reason.trim()) {
      showCustomAlert('error', 'Personel adı ve gidiş nedeni zorunludur.');
      return;
    }

    setSubmittingAssign(true);
    try {
      await api.post(`/istanbul-office/vehicles/${assignmentForm.vehicle_id}/assign`, {
        driver_name: assignmentForm.driver_name.trim(),
        reason: assignmentForm.reason.trim(),
      });

      showCustomAlert('success', 'Araç çıkışı yapıldı.');
      setAssignmentForm({ vehicle_id: '', driver_name: '', reason: '' });
      refreshData();
    } catch (error) {
      console.error('Araç atama hatası:', error);
      showCustomAlert(
        'error',
        error.response?.data?.detail || 'Araç atama işlemi başarısız.'
      );
    } finally {
      setSubmittingAssign(false);
    }
  };

  const openAssignmentEditModal = (task) => {
    setEditingAssignment({
      id: task.id,
      driver_name: task.driver_name || '',
      reason: task.reason || '',
    });
    setIsAssignEditModalOpen(true);
  };

  const handleUpdateAssignment = async (e) => {
    e.preventDefault();

    if (!editingAssignment.driver_name.trim() || !editingAssignment.reason.trim()) {
      showCustomAlert('error', 'Alanlar boş bırakılamaz.');
      return;
    }

    setSubmittingAssignmentEdit(true);
    try {
      await api.put(
        `/istanbul-office/vehicles/assignments/${editingAssignment.id}`,
        {
          driver_name: editingAssignment.driver_name.trim(),
          reason: editingAssignment.reason.trim(),
        }
      );

      showCustomAlert('success', 'Görev detayları güncellendi.');
      setIsAssignEditModalOpen(false);
      refreshData();
    } catch (error) {
      console.error('Görev güncelleme hatası:', error);
      showCustomAlert(
        'error',
        error.response?.data?.detail || 'Görev güncelleme başarısız.'
      );
    } finally {
      setSubmittingAssignmentEdit(false);
    }
  };

  const requestReturn = (id) => {
    setConfirmInfo({ show: true, id });
  };

  const confirmReturn = async () => {
    if (!confirmInfo.id) return;

    setSubmittingReturn(true);
    try {
      await api.put(`/istanbul-office/vehicles/assignments/${confirmInfo.id}/return`);
      showCustomAlert('success', 'Araç teslim alındı.');
      refreshData();
    } catch (error) {
      console.error('Araç teslim alma hatası:', error);
      showCustomAlert(
        'error',
        error.response?.data?.detail || 'İşlem başarısız.'
      );
    } finally {
      setSubmittingReturn(false);
      setConfirmInfo({ show: false, id: null });
    }
  };

  const formatTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const availableVehicles = fleet.filter((car) => car.is_available);

  return (
    <Box
      style={{
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        style={{
          position: 'absolute',
          top: '-140px',
          right: '-120px',
          width: '340px',
          height: '340px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
          filter: 'blur(2px)',
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: '-180px',
          left: '-120px',
          width: '380px',
          height: '380px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          filter: 'blur(2px)',
        }}
      />

      <Container size="xl" style={{ position: 'relative', zIndex: 2 }}>
        <Paper
          radius="xl"
          p="lg"
          mb="xl"
          bg="rgba(255,255,255,0.10)"
          style={{
            border: '1px solid rgba(255,255,255,0.18)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 20px 45px rgba(6, 25, 45, 0.20)',
          }}
        >
          <Group justify="space-between" align="center">
            <Group gap="md">
              <ThemeIcon
                size={64}
                radius="xl"
                variant="gradient"
                gradient={{ from: '#dbeafe', to: '#ffffff', deg: 135 }}
                style={{ boxShadow: '0 12px 28px rgba(3, 24, 46, 0.28)' }}
              >
                <IconCar size={34} stroke={1.8} color="#0b5d9e" />
              </ThemeIcon>

              <div>
                <Badge radius="sm" size="md" variant="light" color="blue">
                    İstanbul Ofis
                </Badge>
                <Title order={2} c="black" fw={900}>
                  Araç Takip
                </Title>
                <Text c="blue.7" size="sm">
                  Araçlara görev çıkışı verin ve aktif görevleri takip edin.
                </Text>
              </div>
            </Group>

            <Group>
              <Button
                variant="light"
                color="gray"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate('/istanbul-office/dashboard')}
              >
                Panele Dön
              </Button>

              <Button
                variant="light"
                color="blue"
                leftSection={<IconRefresh size={16} />}
                onClick={refreshData}
              >
                Yenile
              </Button>

              <Button
                variant="gradient"
                gradient={{ from: '#0b5d9e', to: '#1c7ed6', deg: 120 }}
                leftSection={<IconPlus size={16} />}
                onClick={() => setIsFormModalOpen(true)}
              >
                Yeni Araç Tanımla
              </Button>
            </Group>
          </Group>
        </Paper>

        {alertInfo.show && (
          <Notification
            mb="lg"
            radius="md"
            withBorder
            color={alertInfo.type === 'success' ? 'teal' : 'red'}
            icon={
              alertInfo.type === 'success' ? (
                <IconCheck size="1.1rem" />
              ) : (
                <IconX size="1.1rem" />
              )
            }
            onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
          >
            {alertInfo.message}
          </Notification>
        )}

        <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="lg">
          <Paper
            p="xl"
            radius="xl"
            bg="rgba(255,255,255,0.96)"
            style={{
              border: '1px solid rgba(255,255,255,0.55)',
              boxShadow: '0 24px 55px rgba(6, 25, 45, 0.18)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Group justify="space-between" mb="lg">
              <div>
                <Text fw={700} size="lg">
                  Araç Filosu
                </Text>
                <Text size="sm" c="dimmed">
                  Toplam araç: {fleet.length}
                </Text>
              </div>

              <Badge size="lg" radius="sm" color="blue" variant="light">
                Müsait: {availableVehicles.length}
              </Badge>
            </Group>

            {loading ? (
              <Center py={80}>
                <Stack align="center" gap="xs">
                  <Loader color="blue" />
                  <Text size="sm" c="dimmed">
                    Araç listesi yükleniyor...
                  </Text>
                </Stack>
              </Center>
            ) : fleet.length === 0 ? (
              <Center py={80}>
                <Stack align="center" gap="xs">
                  <ThemeIcon size={56} radius="xl" color="gray" variant="light">
                    <IconCar size={26} />
                  </ThemeIcon>
                  <Text fw={600}>Henüz araç kaydı yok.</Text>
                  <Text size="sm" c="dimmed">
                    Yeni araçlar burada listelenecek.
                  </Text>
                </Stack>
              </Center>
            ) : (
              <Stack gap="md">
                {fleet.map((car) => (
                  <Card
                    key={car.id}
                    radius="xl"
                    withBorder
                    p="lg"
                    shadow="sm"
                    style={{
                      background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                    }}
                  >
                    <Group justify="space-between" align="center" wrap="wrap">
                      <Stack gap={4}>
                        <Text fw={700} size="md">
                          {car.plate}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {car.model}
                        </Text>
                      </Stack>

                      <Group gap="sm">
                        <Badge
                          color={car.is_available ? 'teal' : 'yellow'}
                          variant="light"
                          radius="sm"
                        >
                          {car.is_available ? 'Müsait' : 'Görevde'}
                        </Badge>

                        <Button
                          variant="light"
                          color="yellow"
                          leftSection={<IconEdit size={16} />}
                          onClick={() => openEditModal(car)}
                        >
                          Düzenle
                        </Button>

                        <Button
                          variant="light"
                          color="red"
                          leftSection={<IconTrash size={16} />}
                          onClick={() => setDeleteConfirmInfo({ show: true, id: car.id })}
                        >
                          Sil
                        </Button>
                      </Group>
                    </Group>
                  </Card>
                ))}
              </Stack>
            )}
          </Paper>

          <Paper
            p="xl"
            radius="xl"
            bg="rgba(255,255,255,0.96)"
            style={{
              border: '1px solid rgba(255,255,255,0.55)',
              boxShadow: '0 24px 55px rgba(6, 25, 45, 0.18)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Group mb="md">
              <ThemeIcon size={50} radius="xl" color="teal" variant="light">
                <IconRoad size={24} />
              </ThemeIcon>
              <div>
                <Text fw={700} size="lg">
                  Görev Çıkışı
                </Text>
                <Text size="sm" c="dimmed">
                  Müsait araçlardan birini personele atayın.
                </Text>
              </div>
            </Group>

            <form onSubmit={handleAssign}>
              <Stack gap="md">
                <Select
                  label="Araç Seçiniz"
                  placeholder="Müsait araç seçin"
                  value={assignmentForm.vehicle_id}
                  onChange={(value) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      vehicle_id: value || '',
                    })
                  }
                  data={availableVehicles.map((car) => ({
                    value: String(car.id),
                    label: `${car.plate} - ${car.model}`,
                  }))}
                  searchable
                  radius="md"
                />

                <TextInput
                  label="Personel Adı"
                  value={assignmentForm.driver_name}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      driver_name: e.currentTarget.value,
                    })
                  }
                  required
                  radius="md"
                />

                <TextInput
                  label="Gidiş Nedeni"
                  value={assignmentForm.reason}
                  onChange={(e) =>
                    setAssignmentForm({
                      ...assignmentForm,
                      reason: e.currentTarget.value,
                    })
                  }
                  required
                  radius="md"
                />

                <Button
                  type="submit"
                  fullWidth
                  mt="sm"
                  size="md"
                  radius="md"
                  loading={submittingAssign}
                  variant="gradient"
                  gradient={{ from: 'teal', to: 'cyan', deg: 120 }}
                  leftSection={<IconRoad size={18} />}
                >
                  Çıkış Ver
                </Button>
              </Stack>
            </form>
          </Paper>

          <Paper
            p="xl"
            radius="xl"
            bg="rgba(255,255,255,0.96)"
            style={{
              border: '1px solid rgba(255,255,255,0.55)',
              boxShadow: '0 24px 55px rgba(6, 25, 45, 0.18)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Group justify="space-between" mb="lg">
              <div>
                <Text fw={700} size="lg">
                  Sahadaki Araçlar
                </Text>
                <Text size="sm" c="dimmed">
                  Aktif görev sayısı: {activeAssignments.length}
                </Text>
              </div>

              <Badge size="lg" radius="sm" color="yellow" variant="light">
                Aktif: {activeAssignments.length}
              </Badge>
            </Group>

            {loading ? (
              <Center py={80}>
                <Stack align="center" gap="xs">
                  <Loader color="blue" />
                  <Text size="sm" c="dimmed">
                    Aktif görevler yükleniyor...
                  </Text>
                </Stack>
              </Center>
            ) : activeAssignments.length === 0 ? (
              <Center py={80}>
                <Stack align="center" gap="xs">
                  <ThemeIcon size={56} radius="xl" color="gray" variant="light">
                    <IconTruckReturn size={26} />
                  </ThemeIcon>
                  <Text fw={600}>Dışarıda araç yok.</Text>
                  <Text size="sm" c="dimmed">
                    Aktif görevler burada listelenecek.
                  </Text>
                </Stack>
              </Center>
            ) : (
              <Stack gap="md">
                {activeAssignments.map((task) => (
                  <Card
                    key={task.id}
                    radius="xl"
                    withBorder
                    p="lg"
                    shadow="sm"
                    style={{
                      background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                    }}
                  >
                    <Group justify="space-between" align="flex-start" wrap="wrap" mb="sm">
                      <Text fw={700} size="md">
                        {task.vehicle?.plate || '-'}
                      </Text>

                      <Button
                        variant="light"
                        color="yellow"
                        leftSection={<IconEdit size={16} />}
                        onClick={() => openAssignmentEditModal(task)}
                      >
                        Düzenle
                      </Button>
                    </Group>

                    <Stack gap={6}>
                      <Text size="sm" c="dimmed">
                        <Text span fw={600} c="dark">
                          Sürücü:
                        </Text>{' '}
                        {task.driver_name}
                      </Text>

                      <Text size="sm" c="dimmed">
                        <Text span fw={600} c="dark">
                          Neden:
                        </Text>{' '}
                        {task.reason}
                      </Text>

                      <Text size="sm" c="dimmed">
                        <Text span fw={600} c="dark">
                          Çıkış:
                        </Text>{' '}
                        {formatTime(task.out_time)}
                      </Text>
                    </Stack>

                    <Button
                      fullWidth
                      mt="md"
                      color="red"
                      leftSection={<IconTruckReturn size={16} />}
                      onClick={() => requestReturn(task.id)}
                    >
                      Teslim Al
                    </Button>
                  </Card>
                ))}
              </Stack>
            )}
          </Paper>
        </SimpleGrid>
      </Container>

      <Modal
        opened={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        title="Yeni Araç Ekle"
        centered
        radius="xl"
        size="lg"
      >
        <form onSubmit={handleAddVehicle}>
          <Stack gap="md">
            <TextInput
              label="Plaka"
              value={newVehicle.plate}
              onChange={(e) =>
                setNewVehicle({ ...newVehicle, plate: e.currentTarget.value })
              }
              required
              radius="md"
            />

            <TextInput
              label="Model"
              value={newVehicle.model}
              onChange={(e) =>
                setNewVehicle({ ...newVehicle, model: e.currentTarget.value })
              }
              required
              radius="md"
            />

            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => setIsFormModalOpen(false)}>
                İptal
              </Button>

              <Button
                type="submit"
                loading={submittingVehicle}
                variant="gradient"
                gradient={{ from: '#0b5d9e', to: '#1c7ed6', deg: 120 }}
              >
                Kaydet
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Araç Bilgilerini Düzenle"
        centered
        radius="xl"
        size="lg"
      >
        <form onSubmit={handleUpdateVehicle}>
          <Stack gap="md">
            <TextInput
              label="Plaka"
              value={editingVehicle.plate}
              onChange={(e) =>
                setEditingVehicle({
                  ...editingVehicle,
                  plate: e.currentTarget.value,
                })
              }
              required
              radius="md"
            />

            <TextInput
              label="Model"
              value={editingVehicle.model}
              onChange={(e) =>
                setEditingVehicle({
                  ...editingVehicle,
                  model: e.currentTarget.value,
                })
              }
              required
              radius="md"
            />

            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => setIsEditModalOpen(false)}>
                İptal
              </Button>

              <Button
                type="submit"
                loading={submittingVehicleEdit}
                variant="gradient"
                gradient={{ from: '#0b5d9e', to: '#1c7ed6', deg: 120 }}
              >
                Güncelle
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={isAssignEditModalOpen}
        onClose={() => setIsAssignEditModalOpen(false)}
        title="Görev Detaylarını Düzenle"
        centered
        radius="xl"
        size="lg"
      >
        <form onSubmit={handleUpdateAssignment}>
          <Stack gap="md">
            <TextInput
              label="Personel Adı"
              value={editingAssignment.driver_name}
              onChange={(e) =>
                setEditingAssignment({
                  ...editingAssignment,
                  driver_name: e.currentTarget.value,
                })
              }
              required
              radius="md"
            />

            <TextInput
              label="Gidiş Nedeni"
              value={editingAssignment.reason}
              onChange={(e) =>
                setEditingAssignment({
                  ...editingAssignment,
                  reason: e.currentTarget.value,
                })
              }
              required
              radius="md"
            />

            <Group justify="flex-end" mt="sm">
              <Button
                variant="default"
                onClick={() => setIsAssignEditModalOpen(false)}
              >
                İptal
              </Button>

              <Button
                type="submit"
                loading={submittingAssignmentEdit}
                variant="gradient"
                gradient={{ from: '#0b5d9e', to: '#1c7ed6', deg: 120 }}
              >
                Güncelle
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={confirmInfo.show}
        onClose={() => setConfirmInfo({ show: false, id: null })}
        centered
        radius="xl"
        size="md"
        withCloseButton={false}
      >
        <Center mb="md">
          <ThemeIcon size={68} radius="xl" color="red" variant="light">
            <IconAlertTriangle size={32} />
          </ThemeIcon>
        </Center>

        <Title order={4} ta="center" mb="xs">
          Onaylıyor musunuz?
        </Title>
        <Text ta="center" c="dimmed" mb="lg">
          Araç teslim alınacak.
        </Text>

        <Group justify="center">
          <Button
            variant="default"
            onClick={() => setConfirmInfo({ show: false, id: null })}
          >
            Vazgeç
          </Button>

          <Button
            color="red"
            loading={submittingReturn}
            onClick={confirmReturn}
          >
            Evet
          </Button>
        </Group>
      </Modal>

      {/* YENİ: Araç Silme Onay Modalı */}
      <Modal
        opened={deleteConfirmInfo.show}
        onClose={() => setDeleteConfirmInfo({ show: false, id: null })}
        centered
        radius="xl"
        size="md"
        withCloseButton={false}
      >
        <Center mb="md">
          <ThemeIcon size={68} radius="xl" color="red" variant="light">
            <IconAlertTriangle size={32} />
          </ThemeIcon>
        </Center>

        <Title order={4} ta="center" mb="xs">
          Aracı Sil
        </Title>
        <Text ta="center" c="dimmed" mb="lg">
          Bu aracı filodan silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
        </Text>

        <Group justify="center">
          <Button
            variant="default"
            onClick={() => setDeleteConfirmInfo({ show: false, id: null })}
          >
            Vazgeç
          </Button>

          <Button
            color="red"
            loading={submittingDelete}
            onClick={confirmDeleteVehicle}
          >
            Evet, Sil
          </Button>
        </Group>
      </Modal>
    </Box>
  );
};

export default VehicleTracking;